import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    // List and graph
    'row',
    'cell',
    'filter',
    'table',
    // Clock
    'clock',
    'line',
    'image',
    'dot',
  ];

  connect() {
    // Re-run update() when the theme is toggled (dark <-> light). This
    // re-colorizes the brackets with the new --color-text and, via
    // updateExpanders(), forces the SVG aspect clocks to reflow/re-measure
    // their Expander height — without it the "lösende Aspekte" clock collapses
    // into an empty placeholder on a live theme switch (issue #4382).
    this.onThemeChanged = () => this.update();
    window.addEventListener('theme:changed', this.onThemeChanged);
  }

  disconnect() {
    window.removeEventListener('theme:changed', this.onThemeChanged);
  }

  bracketRequired(currentFilter, klasses, pillarType) {
    if (klasses.includes(pillarType))
      return this.containsSome(currentFilter, [pillarType]);

    const otherExt = {
      lz: ['fj', 'fm'],
      fj: ['lz', 'fm'],
      fm: ['lz', 'fj'],
    };

    if (otherExt[pillarType])
      return otherExt[pillarType].some(
        (type) =>
          this.containsSome(currentFilter, [type]) && klasses.includes(type),
      );

    return false;
  }

  // Expander components (which are currently open) may be resized according the new content
  updateExpanders() {
    const expanderComponents = document.querySelectorAll(
      'section[data-controller="expander--component',
    );
    if (!expanderComponents) return;

    expanderComponents.forEach((expanderComponent) => {
      const expanderController =
        this.application.getControllerForElementAndIdentifier(
          expanderComponent,
          'expander--component',
        );
      if (!expanderController) return;

      if (expanderController.isOpen) {
        expanderController.toggle();
        setTimeout(() => expanderController.toggle(), 200);
      }
    });
  }

  // Draw dots instead of solid bracket when ext pillar is disabled
  dottedBrackets(row, currentFilter, klasses) {
    row
      .querySelectorAll('.js-lz')
      .forEach((bracket) =>
        bracket.classList.toggle(
          'border-dotted',
          !this.bracketRequired(currentFilter, klasses, 'lz'),
        ),
      );
    row
      .querySelectorAll('.js-fj')
      .forEach((bracket) =>
        bracket.classList.toggle(
          'border-dotted',
          !this.bracketRequired(currentFilter, klasses, 'fj'),
        ),
      );
    row
      .querySelectorAll('.js-fm')
      .forEach((bracket) =>
        bracket.classList.toggle(
          'border-dotted',
          !this.bracketRequired(currentFilter, klasses, 'fm'),
        ),
      );
  }

  colorizeBrackets(row, currentFilter, klasses) {
    const brackets = row.querySelectorAll('.js-bracket');
    const title = row.querySelector('.js-title');
    const newElement = row.querySelector('.js-new-element');
    const elementLabel = row.querySelector('.js-element-label');

    if (this.transforming(klasses, currentFilter)) {
      if (newElement) newElement.removeAttribute('style');
      if (elementLabel) {
        elementLabel.removeAttribute('style');
        elementLabel.classList.remove('line-through', 'text-white');
      }
      if (title) title.style.color = null;
      brackets.forEach((bracket) => (bracket.style.borderColor = null));
      return;
    }

    // Hide "transformiert" text in table when not transforming
    if (newElement) newElement.style.display = 'none';
    // Show element label with strikethrough in graph when not transforming
    if (elementLabel) {
      elementLabel.style.display = ''; // Keep visible
      elementLabel.classList.add('line-through');
      elementLabel.classList.remove('text-white');
      elementLabel.style.color = this.defaultcolor();
    }
    if (title) title.style.color = this.defaultcolor();
    brackets.forEach((bracket) => {
      bracket.style.borderColor = this.defaultcolor();
      bracket.style.color = this.defaultcolor();
    });
  }

  update() {
    const currentFilter = this.currentFilter();

    this.rowTargets.forEach((row) => {
      const klasses = row.dataset.klasses.split(',');

      if (
        this.match(klasses, currentFilter) &&
        this.matchSpecial(klasses, currentFilter)
      ) {
        row.removeAttribute('style');
        this.dottedBrackets(row, currentFilter, klasses);
        this.colorizeBrackets(row, currentFilter, klasses);
      } else row.style.display = 'none';
    });

    this.rowTargets.forEach((row) => {
      if (this.isSpecialRedundant(row)) {
        row.style.display = 'none';
      }
    });

    this.clockTargets.forEach((clock) => {
      const klasses = clock.dataset.klasses.split(',');

      let visible = true;

      if (klasses.includes('festigend') && !currentFilter.includes('festigend'))
        visible = false;

      if (klasses.includes('loesend') && !currentFilter.includes('loesend'))
        visible = false;

      if (
        klasses.includes('ohs') &&
        !this.containsSome(currentFilter, ['festigend', 'loesend'])
      )
        visible = false;

      if (visible) clock.removeAttribute('style');
      else clock.style.display = 'none';
    });

    this.lineTargets.forEach((line) => {
      const klasses = line.dataset.klasses.split(',');

      if (this.match(klasses, currentFilter)) line.removeAttribute('style');
      else line.style.display = 'none';
    });

    this.lineTargets.forEach((line) => {
      if (this.isSpecialRedundant(line)) {
        line.style.display = 'none';
      }
    });

    // Highlight symbols for selected pillars only
    this.imageTargets.forEach((image) => {
      // Check for this image if there are pillars selected by filter
      const affectedPillars = image.dataset.pillars.split(',').some((p) => {
        if (p == 'current_life_cycle') return currentFilter.includes('lz');
        if (p == 'current_year') return currentFilter.includes('fj');
        if (p == 'current_month') return currentFilter.includes('fm');
        if (['year', 'month', 'day', 'hour'].includes(p)) return true;
        if (['combo_palace', 'mind_palace', 'soul_palace'].includes(p))
          return true;
      });

      if (affectedPillars || image.dataset.self_element != undefined)
        image.setAttribute('filter', '');
      else
        image.setAttribute(
          'filter',
          `url(#${image.dataset.variant}-grayscale)`,
        );
    });

    // Show dots connected by visible lines only
    this.dotTargets.forEach((dot) => {
      const visibleLines = this.lineTargets.filter(
        (line) =>
          line.dataset.variant === dot.dataset.variant &&
          line.style.display != 'none',
      );

      const symbols = visibleLines
        .map((line) => line.dataset.symbols.split(','))
        .flat();

      if (symbols.includes(dot.dataset.symbol)) dot.removeAttribute('style');
      else dot.style.display = 'none';
    });

    const selectedPillars = {
      hour: true,
      day: true,
      month: true,
      year: true,

      current_life_cycle: currentFilter.includes('lz'),
      current_year: currentFilter.includes('fj'),
      current_month: currentFilter.includes('fm'),

      mind_palace: true,
      combo_palace: true,
      soul_palace: true,
    };

    this.cellTargets.forEach((target) => {
      if (selectedPillars[target.dataset.pillar])
        target.classList.remove('hidden-cell');
      else target.classList.add('hidden-cell');
    });

    this.tableTargets.forEach((target) => {
      const visibleRows = target.querySelectorAll(
        'tbody tr:not([style*="display:none"]):not([style*="display: none"])',
      );

      if (visibleRows.length == 0) target.style.display = 'none';
      else target.style.display = 'block';
    });

    this.updateExpanders();
  }

  currentFilter() {
    if (!this.hasFilterTarget)
      // No filter button present, so use default filter
      return this.defaultFilter;

    return this.filterTargets
      .filter((target) => target.checked)
      .map((target) => target.dataset.filter);
  }

  filterString(filter) {
    const sortBy = ['lz', 'fj', 'fm'];

    return filter
      .filter((f) => sortBy.includes(f))
      .sort((a, b) => sortBy.indexOf(a) - sortBy.indexOf(b))
      .join('-');
  }

  matchSpecial(klasses, filter) {
    if (filter.includes('lz')) {
      // Hide special variants created for missing current_life_cycle
      if (
        klasses.includes('special_removed-current_life_cycle') &&
        klasses.includes('special_variant')
      )
        return false;
    } else {
      // Show special variants created for current_life_cycle ONLY
      if (klasses.includes('special_variant'))
        if (klasses.includes('special_removed-current_life_cycle')) return true;

      // Hide special originals using current_life_cycle
      if (klasses.includes('special_original') && klasses.includes('lz'))
        return false;
    }

    if (filter.includes('fj')) {
      // Hide special variants created for missing current_year
      if (
        klasses.includes('special_removed-current_year') &&
        klasses.includes('special_variant')
      )
        return false;
    } else {
      // Show special variants created for current_year ONLY
      if (klasses.includes('special_variant'))
        if (klasses.includes('special_removed-current_year')) return true;

      // Hide special originals using current_year
      if (klasses.includes('special_original') && klasses.includes('fj'))
        return false;
    }

    if (filter.includes('fm')) {
      // Hide special variants created for missing current_month
      if (
        klasses.includes('special_removed-current_month') &&
        klasses.includes('special_variant')
      )
        return false;
    } else {
      // Show special variants created for current_month ONLY
      if (klasses.includes('special_variant'))
        if (klasses.includes('special_removed-current_month')) return true;

      // Hide special originals using current_month
      if (klasses.includes('special_original') && klasses.includes('fm'))
        return false;
    }

    return true;
  }

  isSpecialRedundant(row) {
    const klasses = row.dataset.klasses.split(',');
    if (!klasses.includes('special_variant')) return false;

    const pillarTypes = klasses.filter((k) =>
      ['hour', 'day', 'month', 'year', 'fj', 'fm', 'lz'].includes(k),
    );

    let lookforType = null;
    if (klasses.includes('type-H')) lookforType = 'type-J';
    else if (klasses.includes('type-A')) lookforType = 'type-T';
    else if (klasses.includes('type-3D')) lookforType = null;
    else if (klasses.includes('type-3DE')) lookforType = null;
    if (!lookforType) return false;

    // Is there a visible aspect with type == lookForType and at least two of the same pillars?
    return this.rowTargets.some((otherRow) => {
      if (otherRow.style.display === 'none') return false;

      const rowKlasses = otherRow.dataset.klasses.split(',');
      if (!rowKlasses.includes(lookforType)) return false;

      const rowPillarTypes = rowKlasses.filter((k) =>
        ['hour', 'day', 'month', 'year', 'fj', 'fm', 'lz'].includes(k),
      );
      const intersection = this.intersect(pillarTypes, rowPillarTypes);
      return intersection.length >= 2;
    });
  }

  intersect(a, b) {
    return a.filter(Set.prototype.has, new Set(b));
  }

  isSpecialOriginal(klasses) {
    return klasses.toString().includes('special_original');
  }

  match(klasses, filter) {
    // Klassifizierung
    if (!filter.includes('festigend') && klasses.includes('festigend'))
      return false;
    if (!filter.includes('loesend') && klasses.includes('loesend'))
      return false;
    if (!filter.includes('weitere') && klasses.includes('weitere'))
      return false;

    // Pillars
    const involvedPillars = klasses.filter((k) =>
      ['hour', 'day', 'month', 'year', 'lz', 'fj', 'fm'].includes(k),
    );

    switch (this.filterString(filter)) {
      // 1
      case '':
        return (
          // S1 = {J,M,T,H} UND S2 = {J,M,T,H}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour'],
          ) ||
          // S1 = {J,M,T,H} UND S2 = {J,M,T,H} UND S3 = {J,M,T,H}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour'],
          )
        );

      // 2
      case 'fm':
        return (
          // S1 = {J,M,T,H} UND S2 = {J,M,T,H,FM}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour', 'fm'],
          ) ||
          // S1 = {J,M,T,H} UND S2 = {J,M,T,H} UND S3 = {J,M,T,H,FM}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour', 'fm'],
          )
        );

      // 3
      case 'fj':
        return (
          // S1 = {J,M,T,H} UND S2 = {J,M,T,H,FJ}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour', 'fj'],
          ) ||
          // S1 = {J,M,T,H} UND S2 = {J,M,T,H} UND S3 = {J,M,T,H,FJ}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour', 'fj'],
          )
        );

      // 4
      case 'fj-fm':
        return (
          // S1= {J,M,T,H,FJ} UND S2 = {J,M,T,H,FJ,FM}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour', 'fj'],
            ['year', 'month', 'day', 'hour', 'fj', 'fm'],
          ) ||
          // S1= {J,M,T,H} UND S2 = {J,M,T,H,FJ} UND S3 = {J,M,T,H,FJ,FM}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour', 'fj'],
            ['year', 'month', 'day', 'hour', 'fj', 'fm'],
          )
        );

      // 5
      case 'lz':
        return (
          // S1 = {J,M,T,H} UND S2 = {J,M,T,H,LZ}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour', 'lz'],
          ) ||
          // S1 = {J,M,T,H} UND S2 = {J,M,T,H} UND S3 = {J,M,T,H, LZ}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour', 'lz'],
          )
        );

      // 6
      case 'lz-fm':
        return (
          // S1 = {J,M,T,H,LZ} UND S2 = {J,M,T,H,LZ,FM}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour', 'lz'],
            ['year', 'month', 'day', 'hour', 'lz', 'fm'],
          ) ||
          // S1 = {J,M,T,H} UND S2 = {J,M,T,H,LZ} UND S3 = {J,M,T,H,LZ,FM}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour', 'lz'],
            ['year', 'month', 'day', 'hour', 'lz', 'fm'],
          )
        );

      // 7
      case 'lz-fj':
        return (
          // S1 = {J,M,T,H,LZ} UND S2 = {J,M,T,H,LZ,FJ}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour', 'lz'],
            ['year', 'month', 'day', 'hour', 'lz', 'fj'],
          ) ||
          // S1 = {J,M,T,H} UND S2 = {J,M,T,H,LZ} UND S3 = {J,M,T,H,LZ,FJ}
          this.hasPillars(
            involvedPillars,
            ['year', 'month', 'day', 'hour'],
            ['year', 'month', 'day', 'hour', 'lz'],
            ['year', 'month', 'day', 'hour', 'lz', 'fj'],
          )
        );

      // 8
      case 'lz-fj-fm':
        // Zeige alle Aspekte
        return true;

      default:
        throw new Error("Can't handle this filter combination: " + filter);
    }
  }

  transforming(klasses, filter) {
    let transformInt;
    let transformExtLZ;
    let transformExtFJ;
    let transformExtFM;
    let transformExtLZFJ;
    let transformExtLZFM;
    let transformExtFJFM;
    let transformExtLZFJFM;

    // https://github.com/AstroDNA/astro-dna-software/issues/2402
    if (klasses.includes('type-A')) {
      transformInt = 'transform-a-int';
      transformExtLZ = 'transform-a-extLZ';
      transformExtFJ = 'transform-a-extFJ';
      transformExtFM = 'transform-a-extFM';
      transformExtLZFJ = 'transform-a-extLZFJ';
      transformExtLZFM = 'transform-a-extLZFM';
      transformExtFJFM = 'transform-a-extFJFM';
      transformExtLZFJFM = 'transform-a-extLZFJFM';
    } else {
      transformInt = 'transform-int';
      transformExtLZ = 'transform-extLZ';
      transformExtFJ = 'transform-extFJ';
      transformExtFM = 'transform-extFM';
      transformExtLZFJ = 'transform-extLZFJ';
      transformExtLZFM = 'transform-extLZFM';
      transformExtFJFM = 'transform-extFJFM';
      transformExtLZFJFM = 'transform-extLZFJFM';
    }

    const transformClasses = klasses.filter((k) =>
      [
        transformInt,
        transformExtLZ,
        transformExtFJ,
        transformExtFM,
        transformExtLZFJ,
        transformExtLZFM,
        transformExtFJFM,
        transformExtLZFJFM,
      ].includes(k),
    );

    switch (this.filterString(filter)) {
      case '':
        return this.containsSome(transformClasses, [transformInt]);

      case 'fm':
        return this.containsSome(transformClasses, [
          transformInt,
          transformExtFM,
        ]);

      case 'fj':
        return this.containsSome(transformClasses, [
          transformInt,
          transformExtFJ,
        ]);

      case 'fj-fm':
        return this.containsSome(transformClasses, [
          transformInt,
          transformExtFJFM,
          transformExtFJ,
          transformExtFM,
        ]);

      case 'lz':
        return this.containsSome(transformClasses, [
          transformInt,
          transformExtLZ,
        ]);

      case 'lz-fm':
        return this.containsSome(transformClasses, [
          transformInt,
          transformExtLZFM,
          transformExtLZ,
          transformExtFM,
        ]);

      case 'lz-fj':
        return this.containsSome(transformClasses, [
          transformInt,
          transformExtLZFJ,
          transformExtLZ,
          transformExtFJ,
        ]);

      case 'lz-fj-fm':
        return this.containsSome(transformClasses, [
          transformInt,
          transformExtLZFJFM,
          transformExtLZFJ,
          transformExtLZFM,
          transformExtFJFM,
          transformExtLZ,
          transformExtFJ,
          transformExtFM,
        ]);

      default:
        throw new Error("Can't handle this filter combination: " + filter);
    }
  }

  containsSome(a, b) {
    return a.some((val) => b.includes(val));
  }

  hasPillars(pillars, s1, s2, s3 = null) {
    const first = pillars.find((p) => s1.includes(p));
    if (!first) return false;

    const second = pillars.find((p) => s2.includes(p) && p !== first);
    if (!second) return false;

    if (s3) {
      const third = pillars.find(
        (p) => s3.includes(p) && p !== first && p !== second,
      );
      if (!third) return false;
    }

    return true;
  }

  get defaultFilter() {
    return ['lz', 'fj', 'fm', 'festigend', 'loesend', 'weitere'];
  }

  isDefaultFilter(filter) {
    return JSON.stringify(filter) === JSON.stringify(this.defaultFilter);
  }

  defaultcolor() {
    if (this.onPrint) return 'black';
    // Use the active theme's text color (navy in light, near-white in dark)
    // instead of a hardcoded white, which is invisible on the light background.
    const themeText = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-text')
      .trim();
    return themeText || 'white';
  }

  get onPrint() {
    return document
      .getElementsByTagName('body')[0]
      .classList.contains('js-print');
  }
}
